import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetPlausibilityResponse } from '@/api/handlers/resources/statements/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { StatementQueryKeys } from './statementQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * Advisory plausibility notices for the statement as of a date.
 *
 * A **second request per statement view**, because the API gives no other way to
 * get them: `StatementResponse` carries no warnings field. That is worth fixing
 * server-side — a consumer that forgets to ask displays a silently unbalanced
 * balance sheet as though it were fine — and is flagged in
 * `src/pages/accounting/README.md`.
 *
 * Deliberately **not** retried and never surfaced as an error. Advisories are
 * additive: if this call fails, the statement still renders and the banner simply
 * has nothing to show. `retry: false` keeps a failing advisory endpoint from
 * holding the screen in a pending state.
 */
export const usePlausibilityQuery = (asOf: string, locale: string) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetPlausibilityResponse>>({
        queryKey: StatementQueryKeys.plausibility(asOf, locale),
        queryFn: () => apiClient.statements.getPlausibility(asOf, { locale }),
        enabled: asOf.length > 0,
        staleTime: STALE_TIME_MS,
        retry: false,
    });
};
